/**
 * ProductListPage — admin products management (STORY-008-007). Search + stock /
 * category filters, a selectable `AdminTable` (checkbox column when the caller
 * holds `marketplace.bulk_import`), a sticky `BulkBar` for bulk stock-state /
 * soft-delete, and a row-activated `ProductDrawer` (detail / edit / delete).
 *
 * The category filter consumes the category tree READ-ONLY (cloned
 * `useCategoryTree`) — category management is OUT of M1 (D7). Like sellers, the
 * admin product LIST endpoint has no free-text `q`, so search is a client-side
 * substring filter on the loaded page (name).
 *
 * SCRUM-165: numbered pagination (1, 2, 3, … + Prev/Next) is likewise
 * client-side, chunking the accumulated cursor-fetched rows into fixed
 * `PAGE_LIMIT`-sized pages — see the `currentPage` state below for why (the
 * backend list endpoint is keyset/cursor-paginated, Addendum §6.4, with no
 * total count and no arbitrary `page=N` — converting it to offset/limit
 * pagination would break page stability under concurrent inserts for every
 * consumer of `repo.list_products`, not just this admin page).
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

import { BottomSheet, Button, GPill } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { AdminTable } from "@/features/admin/components/AdminTable";
import { productStatusTone, stockTone } from "@/features/admin/components/tone";
import { useRequirePermission } from "@/features/admin/hooks/useRequirePermission";
import {
  categoryDisplayName,
  useCategoryTree,
  type Category,
} from "@/features/marketplace/useCategoryTree";
import {
  formatKgs,
  formatKgsRange,
  kgsLocaleForLanguage,
} from "@/lib/format-currency";
import { parseDecimal } from "@/lib/format";
import {
  PRODUCT_STOCK_STATES,
  type AdminProduct,
} from "@/features/admin/products/api";
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  useBulkSetStock,
  useBulkSoftDelete,
  useProducts,
  type ProductFilters,
} from "@/features/admin/products/hooks";
import { BulkBar } from "@/features/admin/products/BulkBar";
import { BulkSetAttributeDialog } from "@/features/admin/products/BulkSetAttributeDialog";
import { ProductDrawer } from "@/features/admin/products/ProductDrawer";

/** A contiguous window of page numbers (≤`size`) centered on `current`,
 * clamped to `[1, total]`. Keeps the pagination nav a fixed, small width
 * regardless of how many pages have loaded so far. */
function pageWindow(current: number, total: number, size = 5): number[] {
  if (total <= size) return Array.from({ length: total }, (_, i) => i + 1);
  let start = Math.max(1, current - Math.floor(size / 2));
  let end = start + size - 1;
  if (end > total) {
    end = total;
    start = end - size + 1;
  }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function flattenCategoryOptions(
  tree: Category[],
  language: string,
): Array<{ id: string; name: string }> {
  const out: Array<{ id: string; name: string }> = [];
  const walk = (nodes: Category[]): void => {
    for (const node of nodes) {
      out.push({
        id: node.id,
        name: `${"  ".repeat(node.depth)}${categoryDisplayName(node, language)}`,
      });
      if (node.children.length) walk(node.children);
    }
  };
  walk(tree);
  return out;
}

const SELECT_CLASS =
  "min-h-[44px] rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]";

/** SCRUM-164: shared "what does the SELLER column display" rule — the
 * joined business name, falling back to the shortened id when a product's
 * seller row wasn't found (outer join miss). Used by both cell rendering
 * and the SCRUM-161 client-side sort below, so the two never drift. */
function sellerDisplayName(p: AdminProduct): string {
  return p.seller_business_name ?? p.seller_id.slice(0, 8);
}

/** SCRUM-161 sortable columns — mirrors the 5 `AdminTable` columns below. */
type SortableColumnKey = "name" | "seller" | "price" | "stock" | "status";
const SORTABLE_COLUMN_KEYS: readonly SortableColumnKey[] = [
  "name",
  "seller",
  "price",
  "stock",
  "status",
];
function isSortableColumnKey(key: string): key is SortableColumnKey {
  return (SORTABLE_COLUMN_KEYS as readonly string[]).includes(key);
}

/** Pure comparator for the client-side column sort (ascending order; the
 * caller negates for descending). Null prices always sort last regardless
 * of direction — a null price has no rank, so it never "wins" a compare. */
function compareProducts(a: AdminProduct, b: AdminProduct, key: SortableColumnKey): number {
  switch (key) {
    case "name":
      return a.name.localeCompare(b.name);
    case "seller":
      return sellerDisplayName(a).localeCompare(sellerDisplayName(b));
    case "price": {
      const av = a.price_kgs == null ? null : parseDecimal(a.price_kgs);
      const bv = b.price_kgs == null ? null : parseDecimal(b.price_kgs);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return av - bv;
    }
    case "stock":
      return a.stock_state.localeCompare(b.stock_state);
    case "status":
      return a.status.localeCompare(b.status);
    default:
      return 0;
  }
}

export function ProductListPage(): ReactElement {
  registerAdminI18n();
  const { t, i18n } = useTranslation(ADMIN_NS);
  const locale = kgsLocaleForLanguage(i18n.language);
  const navigate = useNavigate();

  const [stockState, setStockState] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 300).trim().toLowerCase();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [setAttributeOpen, setSetAttributeOpen] = useState(false);
  // SCRUM-161: the admin products list endpoint has no `order_by` (only a
  // fixed `order=asc|desc` over `created_at`, see the admin marketplace
  // router) — so column-header sort is client-side over the loaded page(s),
  // same posture as the name search filter above. `null` = the server's
  // loaded (newest-first) order.
  const [sortState, setSortState] = useState<{
    key: SortableColumnKey;
    direction: "asc" | "desc";
  } | null>(null);
  // SCRUM-165: 1-indexed page over the client-side-chunked `rows` (see the
  // module docstring). Reset to page 1 below whenever a filter/search/sort
  // changes the underlying row set out from under it.
  const [currentPage, setCurrentPage] = useState(1);
  // SCRUM-241 — how many rows one page shows. A client-side chunk size,
  // not a request param (see PAGE_SIZE_OPTIONS for why).
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const canBulk = useRequirePermission("marketplace.bulk_import");
  // The "Set attribute…" bulk action hits `products:bulk-set-attribute`,
  // which the backend gates on `marketplace.product.edit` (NOT
  // `bulk_import` like the rest of the BulkBar) — gated separately so a
  // `bulk_import`-only admin doesn't see a button that would 403 (review
  // fix). Known follow-up (not fixed here): a `product.edit`-only admin
  // without `bulk_import` still can't reach the BulkBar at all, since it
  // only renders under `canBulk`.
  const canSetAttribute = useRequirePermission("marketplace.product.edit");
  // "Add product" CTA (Task F4) — create is a stricter gate than the list's
  // own `.read` permission, matching the new-route gate in App.tsx.
  const canCreate = useRequirePermission("marketplace.product.edit");

  const filters: ProductFilters = { stockState, categoryId };
  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useProducts(filters);
  const { tree } = useCategoryTree();
  const categoryOptions = useMemo(
    () => flattenCategoryOptions(tree, i18n.language),
    [tree, i18n.language],
  );

  const bulkStock = useBulkSetStock();
  const bulkDelete = useBulkSoftDelete();
  const bulkPending = bulkStock.isPending || bulkDelete.isPending;

  const allProducts: AdminProduct[] = useMemo(
    () => data?.pages.flatMap((p) => p.products) ?? [],
    [data],
  );

  const filteredRows = useMemo(() => {
    if (debouncedSearch === "") return allProducts;
    return allProducts.filter((p) =>
      p.name.toLowerCase().includes(debouncedSearch),
    );
  }, [allProducts, debouncedSearch]);

  // SCRUM-161 — client-side sort over the currently-loaded (and filtered)
  // page(s); see `sortState`'s comment for why this can't just be a server
  // `order_by`.
  const rows = useMemo(() => {
    if (sortState === null) return filteredRows;
    const dir = sortState.direction === "asc" ? 1 : -1;
    return [...filteredRows].sort(
      (a, b) => compareProducts(a, b, sortState.key) * dir,
    );
  }, [filteredRows, sortState]);

  const handleSort = useCallback((key: string): void => {
    if (!isSortableColumnKey(key)) return;
    setSortState((prev) => {
      if (prev && prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  }, []);

  // SCRUM-165 — chunk the sorted/filtered `rows` into fixed `PAGE_LIMIT`
  // pages for the numbered-pagination control below.
  const totalKnownPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const pageRows = useMemo(
    () => rows.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [rows, currentPage, pageSize],
  );

  // A filter/search/sort change swaps out the whole `rows` set — snap back
  // to page 1 rather than stranding the admin on a now-meaningless page
  // number (e.g. page 3 of a search that now only has 1 page of results).
  // `pageSize` joins the list: page 2 of 25 is a different slice than page 2
  // of 50, so keeping the number would silently move the admin to rows they
  // did not ask for (SCRUM-241).
  useEffect(() => {
    setCurrentPage(1);
  }, [stockState, categoryId, debouncedSearch, sortState, pageSize]);

  const goToPage = useCallback(
    (page: number): void => {
      const clamped = Math.max(1, page);
      setCurrentPage(clamped);
      // A page number is at most one `PAGE_LIMIT` chunk past what's already
      // loaded (the nav only ever exposes already-loaded numbers plus one
      // Next step) — so stepping onto it just means fetching the next
      // cursor page first, same underlying call the old "Load more" button
      // made.
      if (clamped > totalKnownPages && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    },
    [totalKnownPages, hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  // A page wider than one fetch page needs several cursor pages before it can
  // be drawn in full. Without this, choosing 100 would show 25 rows and an
  // enabled Next arrow — which reads as "the other 75 are missing".
  useEffect(() => {
    if (rows.length < currentPage * pageSize && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [rows.length, currentPage, pageSize, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allSelected = pageRows.length > 0 && pageRows.every((r) => prev.has(r.id));
      return allSelected ? new Set() : new Set(pageRows.map((r) => r.id));
    });
  }, [pageRows]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const handleApplyStock = (next: string): void => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    bulkStock.mutate(
      { ids, stockState: next },
      {
        onSuccess: (res) => {
          toast.success(t("products.bulk.stockSuccess", { count: res.affected.length }));
          clearSelection();
        },
        onError: () => toast.error(t("error.generic")),
      },
    );
  };

  const handleBulkDelete = (): void => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    bulkDelete.mutate(ids, {
      onSuccess: (res) => {
        toast.success(t("products.bulk.deleteSuccess", { count: res.affected.length }));
        clearSelection();
        setConfirmBulkDelete(false);
      },
      onError: () => toast.error(t("error.generic")),
    });
  };

  // A BAND is quoted as a band. `price_kgs` is only the LOW end when
  // `price_max_kgs` is set (v0052), so quoting it alone states 650 for a
  // product that sells at 650-1 000 — the same defect the chat card and the
  // marketplace grid were already fixed for.
  const priceLabel = (raw: string | null, max?: string | null): string => {
    if (raw == null) return "—";
    const low = parseDecimal(raw);
    const high = max == null ? null : parseDecimal(max);
    return high !== null && high > low
      ? formatKgsRange(low, high, locale)
      : formatKgs(low, locale);
  };

  const columns = [
    { key: "name", label: t("products.columns.name"), sortable: true },
    { key: "seller", label: t("products.columns.seller"), sortable: true },
    { key: "price", label: t("products.columns.price"), align: "right" as const, sortable: true },
    { key: "stock", label: t("products.columns.stock"), sortable: true },
    { key: "status", label: t("products.columns.status"), sortable: true },
  ];

  const renderCell = (p: AdminProduct, key: string): ReactElement | string => {
    switch (key) {
      case "name":
        return <span className="font-medium">{p.name}</span>;
      case "seller":
        return (
          <span className="text-[var(--g-secondary)]" title={p.seller_id}>
            {sellerDisplayName(p)}
          </span>
        );
      case "price":
        return priceLabel(p.price_kgs, p.price_max_kgs);
      case "stock":
        return (
          <GPill tone={stockTone(p.stock_state)} size="sm">
            {t(`products.stock.${p.stock_state}`, { defaultValue: p.stock_state })}
          </GPill>
        );
      case "status":
        return (
          <GPill tone={productStatusTone(p.status)} size="sm">
            {t(`products.status.${p.status}`, { defaultValue: p.status })}
          </GPill>
        );
      default:
        return "";
    }
  };

  const renderMobileCard = (p: AdminProduct): ReactElement => (
    <div className="flex items-start justify-between gap-2">
      <div>
        <div className="font-medium">{p.name}</div>
        <div
          className="mt-0.5 text-sm tabular-nums text-[var(--g-secondary)]"
          title={p.seller_id}
        >
          {sellerDisplayName(p)} · {priceLabel(p.price_kgs, p.price_max_kgs)}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1">
        <GPill tone={stockTone(p.stock_state)} size="sm">
          {t(`products.stock.${p.stock_state}`, { defaultValue: p.stock_state })}
        </GPill>
        <GPill tone={productStatusTone(p.status)} size="sm">
          {t(`products.status.${p.status}`, { defaultValue: p.status })}
        </GPill>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-products-page" className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-xl font-bold">{t("products.title")}</h1>
        {canCreate ? (
          <Button
            data-testid="admin-products-add"
            variant="primary"
            size="md"
            onClick={() => navigate("/admin/products/new")}
            className="min-h-[44px]"
          >
            {t("products.editor.addProduct")}
          </Button>
        ) : null}
      </div>

      {/* Toolbar: search + stock chips + category select */}
      <div className="flex flex-col gap-3">
        <input
          type="search"
          data-testid="admin-products-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("products.searchPlaceholder")}
          aria-label={t("common.search")}
          className="min-h-[44px] w-full max-w-md rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]"
        />
        <div className="flex flex-wrap items-center gap-2" data-testid="admin-products-filters">
          <button
            type="button"
            onClick={() => setStockState(null)}
            aria-pressed={stockState === null}
            className={[
              "min-h-[44px] rounded-full px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]",
              stockState === null
                ? "bg-[var(--g-primary)] text-white"
                : "bg-[var(--g-elevated)] text-[var(--g-secondary)] hover:bg-[var(--g-hairline)]",
            ].join(" ")}
          >
            {t("products.stock.all")}
          </button>
          {PRODUCT_STOCK_STATES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStockState(s)}
              aria-pressed={stockState === s}
              data-testid={`admin-products-filter-${s}`}
              className={[
                "min-h-[44px] rounded-full px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]",
                stockState === s
                  ? "bg-[var(--g-primary)] text-white"
                  : "bg-[var(--g-elevated)] text-[var(--g-secondary)] hover:bg-[var(--g-hairline)]",
              ].join(" ")}
            >
              {t(`products.stock.${s}`)}
            </button>
          ))}
          <label className="sr-only" htmlFor="admin-products-category">
            {t("products.columns.category")}
          </label>
          <select
            id="admin-products-category"
            data-testid="admin-products-category-filter"
            value={categoryId ?? ""}
            onChange={(e) => setCategoryId(e.target.value === "" ? null : e.target.value)}
            className={SELECT_CLASS}
          >
            <option value="">{t("products.filter.allCategories")}</option>
            {categoryOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <AdminTable<AdminProduct>
        columns={columns}
        rows={pageRows}
        rowKey={(p) => p.id}
        renderCell={renderCell}
        renderMobileCard={renderMobileCard}
        onRowActivate={(p) => setSelectedProductId(p.id)}
        rowAriaLabel={(p) => p.name}
        testid="admin-products"
        isLoading={isLoading}
        isError={isError}
        emptyLabel={t("products.empty")}
        errorLabel={t("error.loadFailed")}
        selection={
          canBulk
            ? {
                selectedIds,
                onToggle: toggle,
                onToggleAll: toggleAll,
                selectAllLabel: t("products.bulk.selectAll"),
                rowLabel: (p) => p.name,
              }
            : undefined
        }
        sort={{
          key: sortState?.key ?? null,
          direction: sortState?.direction ?? "asc",
          onSort: handleSort,
          ariaLabel: (column, direction) =>
            direction === "asc"
              ? t("common.sort.ascending", { column: column.label })
              : direction === "desc"
                ? t("common.sort.descending", { column: column.label })
                : t("common.sort.unsorted", { column: column.label }),
        }}
      />

      {/* SCRUM-165: numbered pagination replaces the old "Load more" button —
          see the module docstring for why this chunks the client-side rows
          instead of driving a backend `page=N` param. */}
      {rows.length > 0 && (totalKnownPages > 1 || hasNextPage) ? (
        <nav
          aria-label={t("products.pagination.ariaLabel")}
          data-testid="admin-products-pagination"
          className="flex flex-wrap items-center justify-center gap-1"
        >
          {/* SCRUM-241 — items per page. Sits with the pager because it is the
              same control surface: both answer "which rows am I looking at". */}
          <label className="mr-2 flex items-center gap-2 text-sm text-[var(--g-secondary)]">
            {t("products.pagination.pageSize")}
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              data-testid="admin-products-page-size"
              className="min-h-[44px] rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1}
            aria-label={t("products.pagination.previous")}
            data-testid="admin-products-page-prev"
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-[var(--g-secondary)] hover:bg-[var(--g-hairline)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:opacity-40"
          >
            <ChevronLeft className="size-4" aria-hidden="true" />
          </button>
          {pageWindow(currentPage, totalKnownPages).map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => goToPage(n)}
              aria-label={t("products.pagination.page", { n })}
              aria-current={n === currentPage ? "page" : undefined}
              data-testid={`admin-products-page-${n}`}
              className={[
                "inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]",
                n === currentPage
                  ? "bg-[var(--g-primary)] text-white"
                  : "text-[var(--g-secondary)] hover:bg-[var(--g-hairline)]",
              ].join(" ")}
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= totalKnownPages && !hasNextPage}
            aria-label={t("products.pagination.next")}
            data-testid="admin-products-page-next"
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-[var(--g-secondary)] hover:bg-[var(--g-hairline)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:opacity-40"
          >
            {isFetchingNextPage && currentPage >= totalKnownPages ? (
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            ) : (
              <ChevronRight className="size-4" aria-hidden="true" />
            )}
          </button>
        </nav>
      ) : null}

      {canBulk && selectedIds.size > 0 ? (
        <BulkBar
          count={selectedIds.size}
          isPending={bulkPending}
          onApplyStock={handleApplyStock}
          onDeleteClick={() => setConfirmBulkDelete(true)}
          onSetAttributeClick={() => setSetAttributeOpen(true)}
          canSetAttribute={canSetAttribute}
          onClear={clearSelection}
        />
      ) : null}

      <ProductDrawer
        productId={selectedProductId}
        onClose={() => setSelectedProductId(null)}
      />

      <BulkSetAttributeDialog
        open={setAttributeOpen}
        onClose={() => setSetAttributeOpen(false)}
        productIds={[...selectedIds]}
        onApplied={clearSelection}
      />

      <BottomSheet
        open={confirmBulkDelete}
        onClose={() => setConfirmBulkDelete(false)}
        title={t("products.bulk.confirmTitle")}
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-[var(--g-secondary)]">
            {t("products.bulk.confirmBody", { count: selectedIds.size })}
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmBulkDelete(false)}
              disabled={bulkPending}
              className="min-h-[44px]"
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              data-testid="admin-products-bulk-delete-confirm"
              loading={bulkDelete.isPending}
              onClick={handleBulkDelete}
              className="min-h-[44px]"
            >
              {t("products.bulk.delete")}
            </Button>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
}

export default ProductListPage;
